import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ForgotPasswordComponent } from './account/forgot-password/forgot-password.component';
import { LoginComponent } from './account/login/login.component';
import { ResetPasswordComponent } from './account/reset-password/reset-password.component';
import { ResetSentComponent } from './account/reset-sent/reset-sent.component';
import {
  DevicesComponent,
  ErrorLogsComponent,
  ExperimentsComponent,
} from './dashboard/dashboard.component';
import { ResetSuccessfulComponent } from './account/reset-successful/reset-successful.component';
import { SelectDeviceComponent } from './experiments/select-device/select-device.component';
import { SetParametersComponent } from './experiments/set-parameters/set-parameters.component';
import { AuthGuard } from './_guards/auth.guard';
import { CanDeactivateGuard } from './_guards/can-deactivate.guard';
import {
  EDIT_ACCESS_ROLE_LIST,
  LAB_ADMIN_ROLE_LIST,
  OGI_SUPER_ADMIN_ONLY,
  Role,
  RUN_ACCESS_ROLE_LIST,
  SUPER_ADMIN_ROLE_LIST,
} from './_models/role';
import { DeviceSetupComponent } from './experiments/device-setup/device-setup.component';
import { CreateCalibrationComponent } from './calibration/create-calibration/create-calibration.component';
import { CalibrationDataComponent } from './calibration/calibration-data/calibration-data.component';
import { AddUserComponent } from './user/add-user/add-user.component';
import { UserProfileComponent } from './user/user-profile/user-profile.component';
import { AddUserConfirmationComponent } from './account/add-user-confirmation/add-user-confirmation.component';
import { AddDeviceComponent } from './devices/add-device/add-device.component';
import { DeviceDetailsComponent } from './devices/device-details/device-details.component';
import { BuffersComponent } from './devices/buffers/buffers.component';
import {
  DevicesApprovalComponent,
  LabsApprovalComponent,
  OrganisationsApprovalComponent,
  UsersApprovalComponent,
} from './approvals/approvals.component';
import { AddLabComponent } from './laboratories/add-lab/add-lab.component';
import { LabDetailsComponent } from './laboratories/lab-details/lab-details.component';
import { UpdateUserComponent } from './user/update-user/update-user.component';
import {
  OdCalibrationsComponent,
  OxygenCalibrationsComponent,
  PhCalibrationsComponent,
  SpargingCalibrationsComponent,
} from './devices/device-calibrations/device-calibrations.component';
import { OverviewComponent } from './organisations/overview/overview.component';
import { CreateOrganisationComponent } from './organisations/create-organisation/create-organisation.component';
import { OrganisationComponent } from './organisations/organisation/organisation.component';
import { HelpComponent } from './help/help.component';
import { AboutComponent } from './about/about.component';
import { FluorescenceChannelsComponent } from './devices/fluorescence-channels/fluorescence-channels.component';
import { EditCalibrationComponent } from './calibration/edit-calibration/edit-calibration.component';
import { DeviceMessagesComponent } from './devices/device-messages/device-messages.component';
import { TemplateListComponent } from './experiments/template-list/template-list.component';
import { TemplateComponent } from './experiments/template/template.component';
import { DeviceErrorsComponent } from './experiments/device-errors/device-errors.component';
import { DisplayExperimentComponent } from './experiments/display-experiment/display-experiment.component';
import { ControlExperimentComponent } from './experiments/control-experiment/control-experiment.component';
import { ConfirmEmailComponent } from './account/confirm-email/confirm-email.component';
import { UpdateEmailConfirmationComponent } from './account/update-email-confirmation/update-email-confirmation.component';
import { AwaitingConfirmationComponent } from './account/awaiting-confirmation/awaiting-confirmation.component';
import { ForbiddenComponent } from './forbidden/forbidden.component';
import { AdministrationUsersComponent } from './administration/users/users.component';
import { AdministrationDevicesComponent } from './administration/devices/devices.component';
import { TwoFaEnrollmentComponent } from './account/2fa-enrollment/2fa-enrollment.component';
import { TwoFaLoginChallengeComponent } from './account/2fa/2fa.component';

export interface RouteData {
  roles: Role[];
  hideNavbar?: boolean;
}

interface CustomRoute extends Routes {
  data?: RouteData;
}

const routes: CustomRoute = [
  {
    path: 'calibration',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'create-calibration',
        component: CreateCalibrationComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
        data: { roles: EDIT_ACCESS_ROLE_LIST },
      },
      {
        path: ':calibrationType/:id/calibration-data',
        component: CalibrationDataComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
      },
      {
        path: ':calibrationType/:id/edit',
        component: EditCalibrationComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
        data: { roles: EDIT_ACCESS_ROLE_LIST },
      },
    ],
  },
  {
    path: 'laboratories',
    children: [
      {
        path: 'add-lab',
        component: AddLabComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
        data: { roles: SUPER_ADMIN_ROLE_LIST },
      },
      {
        path: 'view-lab',
        component: LabDetailsComponent,
        canActivate: [AuthGuard],
      },
    ],
  },
  {
    path: 'experiment',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'templates',
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            component: TemplateListComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'new',
            component: TemplateComponent,
            canActivate: [AuthGuard],
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
            data: { roles: RUN_ACCESS_ROLE_LIST },
          },
          {
            path: ':id',
            canActivate: [AuthGuard],
            children: [
              {
                path: 'edit',
                component: TemplateComponent,
                canActivate: [AuthGuard],
                canDeactivate: [CanDeactivateGuard],
                pathMatch: 'full',
              },
            ],
          },
        ],
      },
      {
        path: ':id',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'display',
            component: DisplayExperimentComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'control',
            component: ControlExperimentComponent,
            canActivate: [AuthGuard],
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
          },
          {
            path: 'device-errors',
            component: DeviceErrorsComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'set-parameters',
            component: SetParametersComponent,
            canActivate: [AuthGuard],
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
            data: { roles: RUN_ACCESS_ROLE_LIST },
          },
          {
            path: 'select-device',
            component: SelectDeviceComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
            data: { roles: RUN_ACCESS_ROLE_LIST },
          },
          {
            path: 'device-setup',
            component: DeviceSetupComponent,
            canActivate: [AuthGuard],
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
            data: { roles: RUN_ACCESS_ROLE_LIST },
          },
        ],
      },
    ],
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'devices',
        component: DevicesComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
      },
      {
        path: 'experiments',
        component: ExperimentsComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
      },
      {
        path: 'error-logs',
        component: ErrorLogsComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
      },
      {
        path: '',
        redirectTo: '/dashboard/devices',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'approvals',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'labs',
        component: LabsApprovalComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: 'users',
        component: UsersApprovalComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: 'devices',
        component: DevicesApprovalComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: 'organisations',
        component: OrganisationsApprovalComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: '',
        redirectTo: '/approvals/labs',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'user',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'update-user/:id',
        component: UpdateUserComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: SUPER_ADMIN_ROLE_LIST },
      },
      {
        path: 'add-user',
        component: AddUserComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
        data: {
          roles: [
            Role.OGI_Super_Administrator,
            Role.Super_User,
            Role.Lab_Administrator,
          ],
        },
      },
    ],
  },
  {
    path: 'account',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'user-profile',
        component: UserProfileComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'help',
    canActivate: [AuthGuard],
    component: HelpComponent,
    pathMatch: 'full',
    data: { roles: SUPER_ADMIN_ROLE_LIST },
  },
  {
    path: 'about',
    canActivate: [AuthGuard],
    component: AboutComponent,
    pathMatch: 'full',
  },
  {
    path: 'device',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'create-device',
        component: AddDeviceComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
        data: { roles: LAB_ADMIN_ROLE_LIST },
      },
      {
        path: ':id',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'details',
            component: DeviceDetailsComponent,
            canActivate: [AuthGuard],
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
          },
          {
            path: 'messages',
            component: DeviceMessagesComponent,
            canActivate: [AuthGuard],
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
            data: { roles: OGI_SUPER_ADMIN_ONLY },
          },
          {
            path: 'od-calibrations',
            component: OdCalibrationsComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'oxygen-calibrations',
            component: OxygenCalibrationsComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'ph-calibrations',
            component: PhCalibrationsComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'sparging-calibrations',
            component: SpargingCalibrationsComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'buffers',
            component: BuffersComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
          {
            path: 'fluorescence-channels',
            component: FluorescenceChannelsComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
          },
        ],
      },
    ],
  },
  {
    path: 'organisations',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'administration',
        component: OverviewComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: 'create-organisation',
        component: CreateOrganisationComponent,
        canActivate: [AuthGuard],
        canDeactivate: [CanDeactivateGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: ':id',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'details',
            component: OrganisationComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
            data: { roles: SUPER_ADMIN_ROLE_LIST },
          },
        ],
      },
    ],
  },
  {
    path: 'administration',
    canActivate: [AuthGuard],
    data: { roles: OGI_SUPER_ADMIN_ONLY },
    children: [
      {
        path: 'users',
        component: AdministrationUsersComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
      {
        path: 'devices',
        component: AdministrationDevicesComponent,
        canActivate: [AuthGuard],
        pathMatch: 'full',
        data: { roles: OGI_SUPER_ADMIN_ONLY },
      },
    ],
  },
  {
    path: 'create-account',
    component: AddUserConfirmationComponent,
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'reset-sent',
    component: ResetSentComponent,
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  {
    path: 'reset-successful',
    component: ResetSuccessfulComponent,
  },
  {
    path: 'confirm-email',
    component: ConfirmEmailComponent,
  },
  {
    path: 'awaiting-confirmation',
    component: AwaitingConfirmationComponent,
  },
  {
    path: 'update-email-confirmation',
    component: UpdateEmailConfirmationComponent,
  },
  {
    path: '2fa-enrollment',
    component: TwoFaEnrollmentComponent,
    data: { hideNavbar: true },
  },
  {
    path: '2fa',
    component: TwoFaLoginChallengeComponent,
    data: { hideNavbar: true },
  },
  {
    path: '403',
    component: ForbiddenComponent,
  },
  { path: '**', redirectTo: 'dashboard/devices' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { enableTracing: false })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
